/**
 * Date Picker Section Component
 * Handles rendering of date picker sections for both edit and display modes
 */

import React, { useEffect, useRef, useState } from "react";
import moment from "moment";
import { DatePicker } from "rsuite";
import { CalendarIcon } from "./SvgIcons.jsx";
import { CSS_CLASSES, DEFAULT_VALUES, INLINE_STYLES } from "../utils/cardBodyConstants.js";
import { isEntryUnlocked } from "../utils/cardBodyDateHelpers.js";

/**
 * Date Picker Section for Edit Mode
 */
export const EditDatePickerSection = ({
  dateEdit,
  setDateEdit,
  setDateOldEdit,
  datePickerRef,
  handleDateIconClick,
  appSettings,
  getLockHoursHandler,
  onClick,
  className = "",
}) => {
  const localContainerRef = useRef(null);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

  useEffect(() => {
    if (!isDatePickerOpen) return;

    const isInsideDatePicker = (target) => {
      if (!(target instanceof Element)) return false;
      return Boolean(
        target.closest(".rs-picker-date-menu") ||
        target.closest(".rs-picker-toggle-active") ||
        target.closest(".rs-picker-open") ||
        target.closest(".custom-date-icon") ||
        localContainerRef.current?.contains(target)
      );
    };

    const handleOutsidePointer = (event) => {
      if (isInsideDatePicker(event.target)) return;
      try {
        if (datePickerRef?.current && typeof datePickerRef.current.close === "function") {
          datePickerRef.current.close();
        }
      } catch (error) {
        console.error("Error while closing the entry date picker:", error);
      }
      setIsDatePickerOpen(false);
    };

    document.addEventListener("mousedown", handleOutsidePointer, true);
    document.addEventListener("touchstart", handleOutsidePointer, { capture: true, passive: true });

    return () => {
      document.removeEventListener("mousedown", handleOutsidePointer, true);
      document.removeEventListener("touchstart", handleOutsidePointer, true);
    };
  }, [isDatePickerOpen, datePickerRef]);

  const openDatePicker = (event) => {
    event?.stopPropagation?.();
    setIsDatePickerOpen(true);
    handleDateIconClick?.(event);
  };

  return (
    <div
      ref={localContainerRef}
      className={`${CSS_CLASSES.DATE_OVERVIEW_SEC} ${CSS_CLASSES.DATE_OVERVIEW_EDIT} ${className}${isDatePickerOpen ? " date-picker-open" : ""}`}
      onClick={onClick}
    >
      <div onClick={openDatePicker} className={CSS_CLASSES.CUSTOM_DATE_ICON}>
        <CalendarIcon />
      </div>
      <DatePicker
        block
        name="date"
        appearance="subtle"
        value={moment(dateEdit, "YYYY-MM-DD HH:mm:ss").toDate()}
        onChange={(value) => {
          setDateOldEdit(moment(dateEdit, "YYYY-MM-DD HH:mm:ss").toDate());
          setDateEdit(value);
        }}
        ref={datePickerRef}
        container={() =>
          localContainerRef.current?.closest(".timer-entry-row") ||
          localContainerRef.current ||
          document.body
        }
        menuClassName="timer-entry-datepicker-menu"
        placement="bottomEnd"
        oneTap
        isoWeek={
          appSettings.weekStartDay &&
          appSettings.weekStartDay === DEFAULT_VALUES.WEEK_START_MONDAY
        }
        shouldDisableDate={(date) => {
          const formattedDate = moment(date)
            .tz(global.config.getTimeZone())
            .format("YYYY-MM-DD HH:mm:ss");
          return !isEntryUnlocked(formattedDate, global.config, getLockHoursHandler);
        }}
        onEnter={() => setIsDatePickerOpen(true)}
        onExit={() => setIsDatePickerOpen(false)}
        onOpen={() => setIsDatePickerOpen(true)}
        onClose={() => setIsDatePickerOpen(false)}
      />
    </div>
  );
};

/**
 * Date Picker Section for Display Mode
 */
export const DisplayDatePickerSection = ({
  onClick,
  className = "",
}) => {
  return (
    <div className={`${CSS_CLASSES.DATE_OVERVIEW_SEC} ${className}`} onClick={onClick}>
      <div className={CSS_CLASSES.CUSTOM_DATE_ICON} style={INLINE_STYLES.CURSOR_POINTER}>
        <CalendarIcon />
      </div>
    </div>
  );
};
